import type { ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Transition } from 'react-transition-group';

import { safeResizeObserver } from '@polyai/common/utils/safeResizeObserver';

import * as Styled from './ActionDrawer.styled';

export interface ActionDrawerProps {
  open: boolean;
  children: ReactElement | ReactElement[];
  text?: ReactNode;
  cornerVariant?: Styled.ActionBarCornerVariant;
  secondaryAction?: ReactNode;
  isMainNavOpen?: boolean;
  width?: number;
}

const ActionDrawer = ({
  open,
  children,
  text,
  cornerVariant = 'rounded',
  secondaryAction,
  isMainNavOpen = false,
  width,
}: ActionDrawerProps) => {
  const actionBarRef = useRef<HTMLDivElement>(null);
  const [actionBarHeight, setActionBarHeight] = useState(0);

  const actionBarSizeObserver = useMemo(
    () =>
      safeResizeObserver(([actionBar]) => {
        const { height } = actionBar.target.getBoundingClientRect();
        setActionBarHeight(height);
      }),
    [],
  );

  useLayoutEffect(() => {
    if (actionBarRef.current) {
      const element = actionBarRef.current;

      actionBarSizeObserver.observe(element);

      return () => actionBarSizeObserver.unobserve(element);
    }
  }, [actionBarSizeObserver]);

  const actionDrawerRef = useRef<HTMLDivElement>(null);
  const innerTransitionRef = useRef<HTMLDivElement>(null);
  const fakeDrawerRef = useRef<HTMLDivElement>(null);
  const [actionDrawerHeight, setActionDrawerHeight] = useState(0);

  const actionDrawerSizeObserver = useMemo(
    () =>
      safeResizeObserver(([actionBar]) => {
        const { height } = actionBar.target.getBoundingClientRect();
        setActionDrawerHeight(height);
      }),
    [],
  );

  useLayoutEffect(() => {
    if (actionDrawerRef.current) {
      const element = actionDrawerRef.current;

      actionDrawerSizeObserver.observe(element);

      return () => actionDrawerSizeObserver.unobserve(element);
    }
  }, [actionDrawerSizeObserver]);

  return (
    <div>
      <Transition
        nodeRef={actionDrawerRef}
        in={open || !!secondaryAction}
        timeout={10}
      >
        {state => (
          <Styled.ActionDrawerWrapper
            ref={actionDrawerRef}
            $mainNavOpen={isMainNavOpen}
            $state={state}
          >
            <Transition nodeRef={innerTransitionRef} in={open} timeout={10}>
              {state => (
                <Styled.ForceWidth ref={innerTransitionRef} $width={width}>
                  <Styled.Container $barHeight={actionBarHeight} $state={state}>
                    {secondaryAction && (
                      <Styled.SecondaryAction>
                        {secondaryAction}
                      </Styled.SecondaryAction>
                    )}
                    <Styled.ActionBar
                      ref={actionBarRef}
                      $barHeight={actionBarHeight}
                      $cornerVariant={cornerVariant}
                      $hasText={!!text}
                      $state={state}
                      data-test-id="action-drawer"
                    >
                      {text && (
                        <Styled.Text $cornerVariant={cornerVariant}>
                          {text}
                        </Styled.Text>
                      )}
                      <Styled.Buttons>{children}</Styled.Buttons>
                    </Styled.ActionBar>
                  </Styled.Container>
                </Styled.ForceWidth>
              )}
            </Transition>
          </Styled.ActionDrawerWrapper>
        )}
      </Transition>
      <Transition
        nodeRef={fakeDrawerRef}
        in={open || !!secondaryAction}
        timeout={10}
      >
        {state => (
          <Styled.FakeActionDrawer
            ref={fakeDrawerRef}
            $drawerHeight={actionDrawerHeight}
            $state={state}
          />
        )}
      </Transition>
    </div>
  );
};

export default ActionDrawer;
